import React, { useState, useEffect, useCallback, useRef } from "react";
import GraphContainer from "./GraphContainer";
import PieCharts from "./GraphContainer/PieCharts";
import CommonFilter from "../../../atoms/CommonFilter";
import DateRangePickerShortCuts from "../../../organisms/dateRangePicker";
import { addHours, startOfDay, subDays } from "rsuite/esm/internals/utils/date";
import {
  getEmployeeList,
  getAllProjects,
  getTeamsList,
  getBusinessSegmentList,
  getClientList,
} from "../../../../services/basicGets";
import { useCookies } from "react-cookie";
import { getData } from "../../../../services";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { handleClickDownload } from "../../../../services/downloadService";
import "jspdf-autotable";
import { formatHoursToHm } from "../../../../helpers/formatTime";
import autoTable from "jspdf-autotable";
// Import lucide icons that are already used in your project
import { Loader, ChevronDown, FileText, Download, Layers } from "lucide-react";
import LogoMain from "../../../../assets/branding/logo.svg";
// Import only Button and Menu from MUI
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DashboardWidget from "../../../atoms/DashboardWidget";
import styles from "./styles.module.scss";

const Reports = () => {
  const defaultToday = new Date();
  const [cookies] = useCookies(["e_t"]);
  const [filters, setFilters] = useState({});
  const [date, setDate] = useState({
    start_date: addHours(startOfDay(subDays(defaultToday, 14)), 12),
    end_date: addHours(startOfDay(defaultToday), 12),
  });
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [Client, setClient] = useState([]);
  const [projectAnalysis, setProjectAnalysis] = useState([]);
  const [clientAnalysis, setClientAnalysis] = useState([]);
  const [BusinessSegment, setBusinessSegment] = useState([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [timeReportData, setTimeReportData] = useState([]);
  const [reportType, setReportType] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState();
  // MUI Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Handle opening the dropdown menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing the dropdown menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const accessData = useSelector((state) => state.accessDetails?.accessData);
  const isAdmin = accessData?.role?.label === "Admin";

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleDownloadExcel = async () => {
    setReportType("excel");
    setIsGeneratingReport(true);
    handleMenuClose();

    try {
      const res = await getData({
        endpoint: "Report/getProjectLogsReport",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          projects: JSON.stringify(filters?.project),
          employees: JSON.stringify(filters?.employee),
          teams: JSON.stringify(filters?.team),
          client_ids: JSON.stringify(filters?.client),
          businessSegment: JSON.stringify(filters?.business_segment),
          employee_id: isAdmin ? null : cookies.euid,
        },
        token: cookies.e_t,
      });

      if (res) {
        await handleClickDownload(res, "Tms Report");
      }
    } catch (err) {
      console.error("Error downloading Excel report:", err);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const getProjectAnalysis = useCallback(async () => {
    try {
      const res = await getData({
        endpoint: "Dashboard/getProjectAnalysis",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          project_ids: JSON.stringify(filters?.project),
          employee_ids: JSON.stringify(filters?.employee),
          team_ids: JSON.stringify(filters?.team),
          employee_id: isAdmin ? null : cookies.euid,
          client_ids: JSON.stringify(filters?.client),
          businessSegment: JSON.stringify(filters?.business_segment),
        },

        token: cookies.e_t,
      });
      if (res) {
        setProjectAnalysis(res?.chart_data?.[0]?.data);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    }
  }, [cookies.e_t, date, filters]);

  const getClientAnalysis = useCallback(async () => {
    try {
      const res = await getData({
        endpoint: "Dashboard/getClientAnalysis",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          project_ids: JSON.stringify(filters?.project),
          employee_ids: JSON.stringify(filters?.employee),
          team_ids: JSON.stringify(filters?.team),
          employee_id: isAdmin ? null : cookies.euid,
          client_ids: JSON.stringify(filters?.client),
          businessSegment: JSON.stringify(filters?.business_segment),
        },

        token: cookies.e_t,
      });
      if (res) {
        setClientAnalysis(res?.chart_data?.[0]?.data);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    }
  }, [cookies.e_t, date, filters]);

  const getDashboardSummary = useCallback(async () => {
    try {
      const res = await getData({
        endpoint: "Dashboard/getDashboardSummary",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          project_ids: JSON.stringify(filters?.project),
          employee_ids: JSON.stringify(filters?.employee),
          team_ids: JSON.stringify(filters?.team),
          employee_id: isAdmin ? null : cookies.euid,
          client_ids: JSON.stringify(filters?.client),
          businessSegment: JSON.stringify(filters?.business_segment),
        },

        token: cookies.e_t,
      });
      if (res) {
        setDashboardSummary(res);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    }
  }, [cookies.e_t, date, filters]);

  useEffect(() => {
    (async () => {
      const emp = await getEmployeeList({
        params: {
          ...(!isAdmin && { employee_id: cookies.euid }),
        },
        token: cookies.e_t,
      });
      // const teamRoles = await getTeamsList({
      //   token: cookies.e_t,
      // });
      // setTeams(teamRoles);
      const teamProjects = await getAllProjects({
        params: {
          ...(!isAdmin && { employee_id: cookies.euid }),
        },
        token: cookies.e_t,
      });

      const allTeams = await getTeamsList({
        params: {
          ...(!isAdmin && { employee_id: cookies.euid }),
        },
        token: cookies.e_t,
      });

      const BusinessSegmentRoles = await getBusinessSegmentList({
        token: cookies.e_t,
      });

      const clients = await getClientList({
        token: cookies.e_t,
      });

      setClient(clients?.data);
      setBusinessSegment(BusinessSegmentRoles?.data);
      setProjects(teamProjects?.data);
      setEmployees(emp?.employeeResponseValueLabel);
      setTeams(allTeams?.data?.teams);
    })();
  }, []);

  useEffect(() => {
    getProjectAnalysis();
    getClientAnalysis();
    getDashboardSummary();
  }, [getProjectAnalysis, getClientAnalysis, getDashboardSummary]);

  const filterOptions = [
    {
      id: "project",
      label: "Project",
      type: "multi-select",
      options: projects,
    },
    {
      id: "team",
      label: "Team",
      type: "multi-select",
      options: teams,
    },

    {
      id: "employee",
      label: "Employee",
      type: "multi-select",
      options: employees,
    },
    {
      id: "client",
      label: "Clients",
      type: "multi-select",
      options: Client || [],
    },
    {
      id: "business_segment",
      label: "Business Segment",
      type: "multi-select",
      options: BusinessSegment,
      customStyles: { minWidth: "165px" },
    },
  ];

  const projectData = [
    { name: "p1", value: 50 },
    { name: "p2", value: 30 },
    { name: "p3", value: 20 },
    { name: "p4", value: 15 },
    { name: "p5", value: 10 },
  ];

  const handleDownloadReport = async () => {
    setReportType("pdf");
    setIsGeneratingReport(true);
    const pdf = new jsPDF("portrait", "pt", "a4");

    // Use a standard sans-serif font that looks good
    pdf.setFont("helvetica"); // 'helvetica' is a clean sans-serif font available in jsPDF

    // PDF dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;

    // Starting Y position (will be updated after header is added)
    let yOffset = 100; // Increased to accommodate header

    // Track total pages for footer
    let pageCount = 1;

    // Helper function to create small delay for rendering
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Helper function to get the selected filter names
    const getSelectedFilterNames = () => {
      const filterNames = {
        project: [],
        team: [],
        employee: [],
      };

      // Get project names
      if (filters.project && filters.project.length > 0) {
        filters.project.forEach((projectId) => {
          const project = projects.find((p) => p.value === projectId);
          if (project) filterNames.project.push(project.label);
        });
      }

      // Get team names
      if (filters.team && filters.team.length > 0) {
        filters.team.forEach((teamId) => {
          const team = teams.find((t) => t.value === teamId);
          if (team) filterNames.team.push(team.label);
        });
      }

      // Get employee names
      if (filters.employee && filters.employee.length > 0) {
        filters.employee.forEach((employeeId) => {
          const employee = employees.find((e) => e.value === employeeId);
          if (employee) filterNames.employee.push(employee.label);
        });
      }

      return filterNames;
    };

    // Create header function - called on each page
    const addHeader = () => {
      // Header background rectangle
      pdf.setFillColor(245, 245, 245); // Light gray background
      pdf.rect(0, 0, pageWidth, 80, "F");

      // Add subtle header divider line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 80, pageWidth - margin, 80);

      // Report title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(22);
      pdf.setTextColor(60, 60, 60);
      pdf.text("Time Tracking Report", margin, margin + 30);

      // Date information
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(100, 100, 100);
      pdf.text(
        `Period: ${new Date(date.start_date).toLocaleDateString()} - ${new Date(
          date.end_date
        ).toLocaleDateString()}`,
        margin,
        margin + 50
      );

      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
    };

    // Create footer function - called on each page
    const addFooter = () => {
      const pageNumber = pdf.internal.getNumberOfPages();
      // Footer background rectangle
      pdf.setFillColor(245, 245, 245); // Light gray background
      pdf.rect(0, pageHeight - 40, pageWidth, 40, "F");

      // Add subtle footer divider line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, pageHeight - 40, pageWidth - margin, pageHeight - 40);

      // Company name or copyright
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text(
        "© " + new Date().getFullYear() + " Wecharted",
        margin,
        pageHeight - 20
      );

      // Generation timestamp
      const timestamp = `Generated on: ${new Date().toLocaleString()}`;
      pdf.text(timestamp, margin, pageHeight - 10);

      // Page number
      const pageInfo = `Page ${pageNumber}`;
      const pageInfoWidth =
        (pdf.getStringUnitWidth(pageInfo) * 9) / pdf.internal.scaleFactor;
      pdf.text(pageInfo, pageWidth - margin - pageInfoWidth, pageHeight - 15);

      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
    };

    // Add filters section to the report with improved styling
    const addFiltersSection = () => {
      const filterNames = getSelectedFilterNames();

      // Add a light background for the filters section
      pdf.setFillColor(250, 250, 250);
      pdf.rect(margin, yOffset, contentWidth, 100, "F"); // Increased height

      // Add border with rounded corners effect
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.8);
      pdf.roundedRect(margin, yOffset, contentWidth, 100, 3, 3, "S");

      // Add section title with better styling
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(40, 40, 40);
      pdf.text("APPLIED FILTERS", margin + 15, yOffset + 20);

      // Add a subtle divider under the title
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.line(margin + 15, yOffset + 25, margin + 150, yOffset + 25);

      // Add filter details with better spacing
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(80, 80, 80);

      let yPos = yOffset + 45;
      const leftCol = margin + 15;
      const rightCol = margin + 100;

      // Projects filter
      if (filterNames.project.length > 0) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Projects:", leftCol, yPos);
        pdf.setFont("helvetica", "normal");
        const projectText =
          filterNames.project.length > 0
            ? filterNames.project.join(", ")
            : "All Projects";

        // Handle text wrapping
        const lines = pdf.splitTextToSize(projectText, contentWidth - 120);
        pdf.text(lines, rightCol, yPos);
        yPos += Math.max(15, lines.length * 12);
      }

      // Teams filter
      if (filterNames.team.length > 0) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Teams:", leftCol, yPos);
        pdf.setFont("helvetica", "normal");
        const teamText =
          filterNames.team.length > 0
            ? filterNames.team.join(", ")
            : "All Teams";

        const lines = pdf.splitTextToSize(teamText, contentWidth - 120);
        pdf.text(lines, rightCol, yPos);
        yPos += Math.max(15, lines.length * 12);
      }

      // Employees filter
      if (filterNames.employee.length > 0) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Employees:", leftCol, yPos);
        pdf.setFont("helvetica", "normal");
        const employeeText =
          filterNames.employee.length > 0
            ? filterNames.employee.join(", ")
            : "All Employees";

        const lines = pdf.splitTextToSize(employeeText, contentWidth - 120);
        pdf.text(lines, rightCol, yPos);
        yPos += Math.max(15, lines.length * 12);
      }

      // Update yOffset after adding filters section with extra margin
      yOffset += 110; // Increased from 90 to 110 for more spacing
    };

    // Helper function to check if we need a new page
    const checkForNewPage = (heightNeeded) => {
      if (yOffset + heightNeeded > pageHeight - 50) {
        // Adjusted for footer
        pdf.addPage();
        pageCount++;
        addHeader();
        yOffset = 100; // Reset Y position on new page
        return true;
      }
      return false;
    };

    // Helper function to capture elements
    const captureElement = async (elementId) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.error(`Element with ID "${elementId}" not found`);
        return null;
      }

      try {
        await delay(200);

        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
          onclone: (clonedDoc) => {
            // Make sure the cloned element is visible with fixed dimensions
            const clonedElement = clonedDoc.getElementById(elementId);
            if (clonedElement) {
              clonedElement.style.width = `${element.offsetWidth}px`;
              clonedElement.style.height = `${element.offsetHeight}px`;
              clonedElement.style.display = "block";
              clonedElement.style.position = "relative";
              clonedElement.style.opacity = "1";
              clonedElement.style.visibility = "visible";

              // Hide "View Details" section
              const detailsButtons = clonedElement.querySelectorAll(
                ".report-feature-button"
              );
              detailsButtons.forEach((btn) => {
                btn.style.display = "none";
              });
            }
          },
        });

        const imgData = canvas.toDataURL("image/png");
        // Default image width (used for other charts)
        const imgWidth = contentWidth * 0.9;
        // Use larger width specifically for time report chart
        const finalImgWidth =
          elementId === "time-report-chart" ? contentWidth : imgWidth;
        const imgHeight = (canvas.height * finalImgWidth) / canvas.width;

        return { imgData, imgWidth: finalImgWidth, imgHeight };
      } catch (err) {
        console.error(`Error capturing element ${elementId}:`, err);
        return null;
      }
    };

    try {
      // Add header to first page
      addHeader();

      // Add filters section showing applied filters
      const hasFilters =
        (filters.project && filters.project.length > 0) ||
        (filters.team && filters.team.length > 0) ||
        (filters.employee && filters.employee.length > 0);

      if (hasFilters) {
        addFiltersSection();
        yOffset += 30;
      }

      // Capture Time Report Chart
      // Capture Time Report Chart
      const timeReport = await captureElement("time-report-chart");
      if (timeReport) {
        checkForNewPage(timeReport.imgHeight + 40);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Time Report", margin, yOffset);
        yOffset += 20;

        // Center the image
        const xPos = margin + (contentWidth - timeReport.imgWidth) / 2;
        pdf.addImage(
          timeReport.imgData,
          "PNG",
          xPos,
          yOffset,
          timeReport.imgWidth,
          timeReport.imgHeight
        ); // Added comma
        yOffset += timeReport.imgHeight + 30;
      }

      if (timeReportData && timeReportData.length > 0) {
        checkForNewPage(150);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Time Report - Details", margin, yOffset);
        yOffset += 20;

        autoTable(pdf, {
          head: [["Date", "Logged Time"]],
          body: timeReportData.map((item) => [
            new Date(item.date).toLocaleDateString(),
            formatHoursToHm(item.value || 0),
          ]),
          startY: yOffset,
          margin: { horizontal: margin },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [60, 60, 60],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 248, 248],
          },
          theme: "grid",
          styles: {
            fontSize: 10,
            cellPadding: 6,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > pageCount) {
              pageCount = data.pageNumber;
              addHeader();
            }
            addFooter(data.pageNumber);
            yOffset = data.cursor.y + 30;
          },
        });
      }

      // Capture Projects Analysis Chart
      const projectsChart = await captureElement("projects-pie-chart");
      if (projectsChart) {
        checkForNewPage(projectsChart.imgHeight + 40);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Projects Analysis", margin, yOffset);
        yOffset += 20;

        const xPos = margin + (contentWidth - projectsChart.imgWidth) / 2;
        pdf.addImage(
          projectsChart.imgData,
          "PNG",
          xPos,
          yOffset,
          projectsChart.imgWidth,
          projectsChart.imgHeight
        );
        yOffset += projectsChart.imgHeight + 30;
      }

      // Add Projects Table
      if (projectAnalysis && projectAnalysis.length > 0) {
        checkForNewPage(150);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Projects Analysis - Details", margin, yOffset);
        yOffset += 20;

        const totalProjectHours = projectAnalysis.reduce(
          (sum, item) => sum + (item?.value || 0),
          0
        );

        autoTable(pdf, {
          head: [["Project", "Logged Time", "Percentage"]],
          body: projectAnalysis.map((item) => [
            item.name || "Unnamed Project",
            formatHoursToHm(item.value || 0),
            totalProjectHours > 0
              ? `${((item.value / totalProjectHours) * 100).toFixed(2)}%`
              : "0.00%",
          ]),
          startY: yOffset,
          margin: { horizontal: margin },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [60, 60, 60],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 248, 248],
          },
          theme: "grid",
          styles: {
            fontSize: 10,
            cellPadding: 6,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > pageCount) {
              pageCount = data.pageNumber;
              addHeader();
            }
            addFooter(data.pageNumber);
            yOffset = data.cursor.y + 30;
          },
        });
      }

      // Capture Clients Analysis Chart
      const clientsChart = await captureElement("clients-pie-chart");
      if (clientsChart) {
        checkForNewPage(clientsChart.imgHeight + 40);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Clients Analysis", margin, yOffset);
        yOffset += 20;

        const xPos = margin + (contentWidth - clientsChart.imgWidth) / 2;
        pdf.addImage(
          clientsChart.imgData,
          "PNG",
          xPos,
          yOffset,
          clientsChart.imgWidth,
          clientsChart.imgHeight
        );
        yOffset += clientsChart.imgHeight + 30;
      }

      // Add Clients Table
      if (clientAnalysis && clientAnalysis.length > 0) {
        checkForNewPage(150);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text("Clients Analysis - Details", margin, yOffset);
        yOffset += 20;

        const totalClientHours = clientAnalysis.reduce(
          (sum, item) => sum + (item?.value || 0),
          0
        );

        autoTable(pdf, {
          head: [["Client", "Logged Time", "Percentage"]],
          body: clientAnalysis.map((item) => [
            item.name || "Unnamed Client",
            formatHoursToHm(item.value || 0),
            totalClientHours > 0
              ? `${((item.value / totalClientHours) * 100).toFixed(2)}%`
              : "0.00%",
          ]),
          startY: yOffset,
          margin: { horizontal: margin },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [60, 60, 60],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 248, 248],
          },
          theme: "grid",
          styles: {
            fontSize: 10,
            cellPadding: 6,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > pageCount) {
              pageCount = data.pageNumber;
              addHeader();
            }
            addFooter(data.pageNumber);
            yOffset = data.cursor.y + 30;
          },
        });
      }

      // Add note about downloading Excel for more details on the last page only
      checkForNewPage(80); // Increased height for larger note with padding and background
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13); // Larger font size for professional look
      pdf.setTextColor(80, 80, 80); // Dark gray text

      // Calculate text dimensions for background and border
      const noteText =
        "Note: For more detailed information, please download the Excel file.";
      const textWidth =
        (pdf.getStringUnitWidth(noteText) * 13) / pdf.internal.scaleFactor;
      const textHeight = 20; // Approximate height for 13pt font
      const padding = 10; // Generous padding inside background/border
      const boxWidth = textWidth + 2 * padding;
      const boxHeight = textHeight + 2 * padding;
      const boxX = margin;
      const boxY = yOffset - padding;

      // Draw background
      pdf.setFillColor(245, 245, 245); // Light gray background (matches header/footer)
      pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, "F"); // Filled rounded rectangle

      // Draw border
      pdf.setDrawColor(100, 100, 100); // Dark gray border
      pdf.setLineWidth(0.5);
      pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, "S"); // Stroke rounded rectangle

      // Center the note text horizontally and vertically
      const textX = boxX + (boxWidth - textWidth) / 2; // Center horizontally
      const textY = boxY + boxHeight / 2 + 4; // Center vertically, +4 for baseline adjustment

      // Add the note text with "Note:" in bold
      pdf.setFont("helvetica", "bold");
      pdf.text("Note:", textX, textY);
      const noteLabelWidth =
        (pdf.getStringUnitWidth("Note:") * 13) / pdf.internal.scaleFactor;
      pdf.setFont("helvetica", "normal");
      pdf.text(
        " For more detailed information, please download the Excel file.",
        textX + noteLabelWidth,
        textY
      );

      yOffset += boxHeight + 15; // Extra margin below note

      // Ensure footer is added to the last page (after note if new page was created)
      addFooter(pdf.internal.getNumberOfPages());

      pdf.save(`time-report-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="main-container">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <CommonFilter
          filters={filterOptions}
          onFilterChange={handleFilterChange}
        />

        <div className="d-flex gap-2">
          <DateRangePickerShortCuts setDate={setDate} />

          <div className="d-flex align-items-center">
            {isGeneratingReport ? (
              <Button
                variant="contained"
                color="primary"
                disabled
                startIcon={<Loader size={18} className="animate-spin" />}
                sx={{ minWidth: "200px" }}
              >
                Generating {reportType === "pdf" ? "PDF" : "Excel"} Report...
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleMenuOpen}
                  endIcon={<ChevronDown size={18} />}
                  id="download-report-button"
                  aria-controls={open ? "download-report-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  sx={{
                    minWidth: "200px",
                    borderRadius: "8px",
                    textTransform: "none",
                    padding: "9px",
                    backgroundColor: "#6C5DD3",
                    "&:hover": {
                      backgroundColor: "#6C5DD3",
                    },
                    "&:active": {
                      backgroundColor: "#6C5DD3",
                    },
                  }}
                >
                  Download Report
                </Button>
                <Menu
                  id="download-report-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    "aria-labelledby": "download-report-button",
                  }}
                  PaperProps={{
                    style: {
                      width: "250px",
                      borderRadius: "8px",
                      marginTop: "20px",
                      fontFamily: "Nunito Sans, sans-serif",
                    },
                  }}
                >
                  <MenuItem
                    onClick={async () => {
                      handleMenuClose(); // Close the menu
                      handleDownloadReport(); // Trigger PDF generation
                    }}
                    sx={{
                      display: "flex",
                      gap: 1,
                      color: "#393939",
                      padding: "12px 18px",
                    }}
                  >
                    <FileText size={20} color="#8F95B2" />
                    Download PDF
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      handleDownloadExcel();
                    }}
                    sx={{
                      display: "flex",
                      gap: 1,
                      color: "#393939",
                      padding: "12px 18px",
                    }}
                  >
                    <Download size={20} color="#8F95B2" />
                    Download Excel Report
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className={`${styles.widget_container} ${
          isAdmin ? styles.admin : styles.nonAdmin
        }`}
      >
        <DashboardWidget
          title={"Total Projects"}
          count={dashboardSummary?.total_projects}
          icon={<Layers size={22} color="#6C5DD3" />}
          // onClick={() => navigate("/dashboard/projects")}
        />

        <DashboardWidget
          title={"Total Hours Logged"}
          count={dashboardSummary?.total_time_logged}
          icon={<Layers size={22} color="#6C5DD3" />}
          // onClick={() => navigate("/dashboard/projects")}
        />

        <DashboardWidget
          title={"Total Tasks"}
          count={dashboardSummary?.total_tasks}
          icon={<Layers size={22} color="#6C5DD3" />}
          // onClick={() => navigate("/dashboard/projects")}
        />
      </div>

      <GraphContainer
        id="time-report-chart"
        date={date}
        filters={filters}
        onDataLoad={setTimeReportData}
      />

      <div className="d-flex gap-2">
        <div className="col-12 w-50">
          <PieCharts
            id="projects-pie"
            title="Projects Analysis"
            data={projectAnalysis}
            style={{ width: "100%", height: "400px" }}
          />
        </div>

        <div className="col-12 w-50">
          <PieCharts
            id="clients-pie"
            title="Clients Analysis"
            data={clientAnalysis}
            style={{ width: "100%", height: "400px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default Reports;
